import { useEffect, useMemo, useRef } from 'react';

import { getBackend } from '@/backend';
import type { Unsubscribe } from '@/backend/contracts';
import { useAppStore } from '@/store/useAppStore';

function unsubscribeAll(unsubscribes: Unsubscribe[]): void {
  for (const unsubscribe of unsubscribes) {
    unsubscribe();
  }
}

export function useTranscriptionSync(enabled = true): void {
  const cloudEnabled = useAppStore(state => state.cloud.enabled);
  const orgId = useAppStore(state => state.cloud.orgId);
  const projectId = useAppStore(state => state.cloud.projectId);
  const isAuthInitialized = useAppStore(state => state.auth.initialized);
  const user = useAppStore(state => state.auth.user);
  const activeTranscriptSessionId = useAppStore(state => state.activeTranscriptSessionId);
  const displayedTranscriptSessionIdsBySurface = useAppStore(
    state => state.displayedTranscriptSessionIdsBySurface
  );
  const subscribedTranscriptSessionIds = useMemo(() => (
    Array.from(new Set([
      activeTranscriptSessionId,
      ...Object.values(displayedTranscriptSessionIdsBySurface),
    ].filter((sessionId): sessionId is string => Boolean(sessionId)))).toSorted()
  ), [activeTranscriptSessionId, displayedTranscriptSessionIdsBySurface]);

  const sessionUnsubRef = useRef<Unsubscribe | null>(null);
  const activeSessionUnsubsRef = useRef<Unsubscribe[]>([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (sessionUnsubRef.current) {
      sessionUnsubRef.current();
      sessionUnsubRef.current = null;
    }

    if (!enabled || !cloudEnabled || !orgId || !projectId || !isAuthInitialized || !user) {
      return;
    }

    const client = getBackend().transcription;
    if (!client) {
      return;
    }

    const scope = { orgId, projectId };
    sessionUnsubRef.current = client.subscribeTranscriptSessions(scope, (sessions) => {
      if (!mountedRef.current) return;
      useAppStore.getState().transcriptionActions.setTranscriptSessionsFromRemote(sessions);
    });

    return () => {
      if (sessionUnsubRef.current) {
        sessionUnsubRef.current();
        sessionUnsubRef.current = null;
      }
    };
  }, [enabled, cloudEnabled, orgId, projectId, isAuthInitialized, user]);

  useEffect(() => {
    if (activeSessionUnsubsRef.current.length > 0) {
      unsubscribeAll(activeSessionUnsubsRef.current);
      activeSessionUnsubsRef.current = [];
    }

    if (!enabled || !cloudEnabled || !orgId || !projectId || !isAuthInitialized || !user || subscribedTranscriptSessionIds.length === 0) {
      return;
    }

    const client = getBackend().transcription;
    if (!client) {
      return;
    }

    const scope = { orgId, projectId };
    activeSessionUnsubsRef.current = subscribedTranscriptSessionIds.flatMap(sessionId => [
      client.subscribeTranscriptSegments(scope, sessionId, (segments) => {
        if (!mountedRef.current) return;
        useAppStore.getState().transcriptionActions.setTranscriptSegmentsFromRemote(
          sessionId,
          segments,
        );
      }),
      client.subscribeTranscriptActionAnchors(scope, sessionId, (anchors) => {
        if (!mountedRef.current) return;
        useAppStore.getState().transcriptionActions.setTranscriptAnchorsFromRemote(
          sessionId,
          anchors,
        );
      }),
      client.subscribeSpeakerAttributions(scope, sessionId, (attributions) => {
        if (!mountedRef.current) return;
        useAppStore.getState().transcriptionActions.setSpeakerAttributionsFromRemote(
          sessionId,
          attributions,
        );
      }),
    ]);

    return () => {
      if (activeSessionUnsubsRef.current.length > 0) {
        unsubscribeAll(activeSessionUnsubsRef.current);
        activeSessionUnsubsRef.current = [];
      }
    };
  }, [enabled, cloudEnabled, orgId, projectId, isAuthInitialized, user, subscribedTranscriptSessionIds]);
}
